import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { FileText } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { Button, Card, Icon, SearchInput } from '@/components/ui';
import {
  useAllDocuments,
  type GlobalDocumentSummary,
} from '@/hooks/use-knowledge';

// =============================================================================
// Documents List Page - Global view of all documents in the current tenant
// =============================================================================

type ViewMode = 'list' | 'tree';

export function DocumentsListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<ViewMode>('list');

  const { data, isLoading, isError } = useAllDocuments({
    search: search || undefined,
    page: 1,
    page_size: 200,
  });

  const documents: GlobalDocumentSummary[] = data?.data ?? [];
  const grouped = useMemo(() => groupByKbAndCollection(documents), [documents]);

  return (
    <div className="p-6">
      <PageHeader
        icon={FileText}
        title={t('admin.knowledge.documents.title')}
        subtitle={t('admin.knowledge.documents.subtitle')}
        actions={
          <Button leftIcon="upload" onClick={() => navigate('/knowledge/ingest')}>
            {t('admin.knowledge.documents.upload')}
          </Button>
        }
        testId="documents-header"
        className="mb-6"
      />

      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="max-w-md flex-1">
          <SearchInput
            placeholder={t('admin.knowledge.documents.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
        </div>
        <div className="flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            type="button"
            onClick={() => setMode('list')}
            className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${
              mode === 'list'
                ? 'bg-primary-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
            aria-pressed={mode === 'list'}
          >
            <Icon name="menu" size={14} />
            {t('admin.knowledge.documents.viewList')}
          </button>
          <button
            type="button"
            onClick={() => setMode('tree')}
            className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${
              mode === 'tree'
                ? 'bg-primary-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
            aria-pressed={mode === 'tree'}
          >
            <Icon name="folder-open" size={14} />
            {t('admin.knowledge.documents.viewTree')}
          </button>
        </div>
      </div>

      {isLoading ? (
        <Card className="p-8 text-center">
          <Icon name="loader" size={24} className="mx-auto animate-spin text-gray-400" />
        </Card>
      ) : isError ? (
        <Card className="p-8 text-center text-red-600 dark:text-red-400">
          {t('admin.knowledge.documents.loadFailed')}
        </Card>
      ) : documents.length === 0 ? (
        <Card className="p-12 text-center">
          <Icon name="file" size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('admin.knowledge.documents.noDocuments')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
            {t('admin.knowledge.documents.uploadFirst')}
          </p>
          <Button onClick={() => navigate('/knowledge/ingest')}>
            {t('admin.knowledge.documents.goToIngest')}
          </Button>
        </Card>
      ) : mode === 'list' ? (
        <DocumentsFlatList
          documents={documents}
          onOpen={(d) => navigate(`/knowledge/collections/${d.collection_id}`)}
        />
      ) : (
        <DocumentsTreeView
          grouped={grouped}
          onOpen={(d) => navigate(`/knowledge/collections/${d.collection_id}`)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Flat list view
// =============================================================================

function DocumentsFlatList({
  documents,
  onOpen,
}: {
  documents: GlobalDocumentSummary[];
  onOpen: (doc: GlobalDocumentSummary) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <Th>Name</Th>
              <Th>Knowledge base</Th>
              <Th>Collection</Th>
              <Th>Status</Th>
              <Th>Size</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {documents.map((d) => (
              <tr
                key={d.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                onClick={() => onOpen(d)}
              >
                <Td>
                  <div className="flex items-center gap-2">
                    <Icon name="file" size={14} className="text-gray-400 shrink-0" />
                    <span className="truncate">{d.filename}</span>
                  </div>
                  {d.relative_path && (
                    <div className="text-xs text-gray-400 ml-6 truncate">{d.relative_path}</div>
                  )}
                </Td>
                <Td className="text-sm text-gray-600 dark:text-gray-300">
                  {d.knowledge_base_name ?? '—'}
                </Td>
                <Td className="text-sm text-gray-600 dark:text-gray-300">{d.collection_name}</Td>
                <Td>
                  <StatusPill status={d.status} />
                </Td>
                <Td className="text-sm text-gray-500">{formatSize(d.file_size_bytes)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// =============================================================================
// Tree view (grouped by KB → Collection)
// =============================================================================

type KbGroup = {
  id: string | null;
  name: string;
  collections: CollGroup[];
};
type CollGroup = {
  id: string;
  name: string;
  documents: GlobalDocumentSummary[];
};

function groupByKbAndCollection(docs: GlobalDocumentSummary[]): KbGroup[] {
  const byKb = new Map<string, KbGroup>();
  for (const d of docs) {
    const kbKey = d.knowledge_base_id ?? '__none__';
    let kb = byKb.get(kbKey);
    if (!kb) {
      kb = {
        id: d.knowledge_base_id,
        name: d.knowledge_base_name ?? 'Ungrouped',
        collections: [],
      };
      byKb.set(kbKey, kb);
    }
    let coll = kb.collections.find((c) => c.id === d.collection_id);
    if (!coll) {
      coll = { id: d.collection_id, name: d.collection_name, documents: [] };
      kb.collections.push(coll);
    }
    coll.documents.push(d);
  }
  return Array.from(byKb.values());
}

function DocumentsTreeView({
  grouped,
  onOpen,
}: {
  grouped: KbGroup[];
  onOpen: (doc: GlobalDocumentSummary) => void;
}) {
  return (
    <div className="space-y-3">
      {grouped.map((kb) => (
        <KbNode key={kb.id ?? '__none__'} kb={kb} onOpen={onOpen} />
      ))}
    </div>
  );
}

function KbNode({ kb, onOpen }: { kb: KbGroup; onOpen: (d: GlobalDocumentSummary) => void }) {
  const [open, setOpen] = useState(true);
  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left font-medium hover:bg-gray-50 dark:hover:bg-gray-800/50"
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
        <Icon name="database" size={14} className="text-gray-400" />
        <span>{kb.name}</span>
        <span className="ml-auto text-xs text-gray-400">
          {kb.collections.reduce((a, c) => a + c.documents.length, 0)}
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
          {kb.collections.map((c) => (
            <CollectionNode key={c.id} coll={c} onOpen={onOpen} />
          ))}
        </div>
      )}
    </Card>
  );
}

function CollectionNode({
  coll,
  onOpen,
}: {
  coll: CollGroup;
  onOpen: (d: GlobalDocumentSummary) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-6 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50"
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={12} />
        <Icon name="folder-open" size={14} className="text-gray-400" />
        <span>{coll.name}</span>
        <span className="ml-auto text-xs text-gray-400">{coll.documents.length}</span>
      </button>
      {open && (
        <ul className="pl-12 pr-4 py-2 space-y-1">
          {coll.documents.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:text-primary-600"
              onClick={() => onOpen(d)}
            >
              <Icon name="file" size={12} className="text-gray-400 shrink-0" />
              <span className="truncate">
                {d.relative_path ? `${d.relative_path}/${d.filename}` : d.filename}
              </span>
              <span className="ml-auto text-xs text-gray-400 shrink-0">
                {formatSize(d.file_size_bytes)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      {children}
    </th>
  );
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-gray-900 dark:text-gray-100 ${className}`}>{children}</td>;
}

function StatusPill({ status }: { status: string }) {
  const palette: Record<string, string> = {
    ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    pending: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };
  const cls = palette[status] ?? palette.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function formatSize(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
